import { useEffect, useState } from 'react';
import { Phone, CheckCircle, User, Bot } from 'lucide-react';

export interface HandoverSummary {
  customer_email?: string;
  customer_intent?: string;
  orders_discussed?: string[];
  products_discussed?: string[];
  actions_taken?: string[];
  open_questions?: string;
  cart_items?: string;
  current_page?: string;
}

export interface AgentInfo {
  name: string;
  type: 'ai' | 'human';
  role: string;
}

interface HandoverOverlayProps {
  status: 'transferring' | 'connected';
  summary: HandoverSummary;
  newAgent: AgentInfo;
  onDismiss: () => void;
}

export default function HandoverOverlay({
  status,
  summary,
  newAgent,
  onDismiss
}: HandoverOverlayProps) {
  const [showSummary, setShowSummary] = useState(false);

  // Show summary after a brief delay for better UX
  useEffect(() => {
    if (status === 'transferring') {
      const timer = setTimeout(() => setShowSummary(true), 500);
      return () => clearTimeout(timer);
    }
  }, [status]);

  // Auto-dismiss after connected for a few seconds
  useEffect(() => {
    if (status === 'connected') {
      const timer = setTimeout(onDismiss, 3000);
      return () => clearTimeout(timer);
    }
  }, [status, onDismiss]);

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[200] animate-fade-in">
      <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl transform transition-all animate-scale-in">

        {status === 'transferring' ? (
          <>
            {/* Transfer animation */}
            <div className="flex justify-center mb-6">
              <div className="relative">
                {/* Pulsing background */}
                <div className="w-24 h-24 rounded-full bg-rose-100 animate-pulse" />
                {/* Phone icon */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-16 h-16 rounded-full bg-rose-500 flex items-center justify-center animate-bounce">
                    <Phone className="w-8 h-8 text-white" />
                  </div>
                </div>
                {/* Rotating ring */}
                <div className="absolute inset-0 rounded-full border-4 border-rose-300 border-t-rose-500 animate-spin" style={{ animationDuration: '1.5s' }} />
              </div>
            </div>

            <h2 className="text-xl font-semibold text-center mb-2 text-gray-800">
              Je wordt doorverbonden...
            </h2>
            <p className="text-gray-500 text-center mb-6">
              Even geduld, {newAgent.name} neemt het gesprek over.
            </p>

            {/* Summary card - fades in */}
            {showSummary && (summary.customer_intent || summary.actions_taken?.length) && (
              <div className="bg-gray-50 rounded-xl p-4 text-sm animate-fade-in border border-gray-100">
                <h3 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                  <Bot className="w-4 h-4 text-rose-500" />
                  Gesprekssamenvatting
                </h3>
                <div className="space-y-2 text-gray-600">
                  {summary.customer_intent && (
                    <div>
                      <span className="font-medium text-gray-700">Klantvraag: </span>
                      {summary.customer_intent}
                    </div>
                  )}
                  {summary.actions_taken && summary.actions_taken.length > 0 && (
                    <div>
                      <span className="font-medium text-gray-700">Uitgevoerd: </span>
                      {summary.actions_taken.join(', ')}
                    </div>
                  )}
                  {summary.orders_discussed && summary.orders_discussed.length > 0 && (
                    <div>
                      <span className="font-medium text-gray-700">Bestellingen: </span>
                      {summary.orders_discussed.join(', ')}
                    </div>
                  )}
                  {summary.open_questions && (
                    <div>
                      <span className="font-medium text-gray-700">Open vraag: </span>
                      {summary.open_questions}
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            {/* Connected state */}
            <div className="flex justify-center mb-6">
              <div className="relative">
                <div className="w-24 h-24 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle className="w-12 h-12 text-green-500" />
                </div>
              </div>
            </div>

            <h2 className="text-xl font-semibold text-center mb-2 text-gray-800">
              Verbonden met {newAgent.name}
            </h2>
            <div className="flex items-center justify-center gap-2 mb-6">
              <User className="w-4 h-4 text-gray-400" />
              <span className="text-gray-500">{newAgent.role}</span>
            </div>

            <button
              onClick={onDismiss}
              className="w-full bg-gradient-to-r from-rose-500 to-rose-600 text-white rounded-xl py-3 font-medium hover:from-rose-600 hover:to-rose-700 transition-all shadow-lg hover:shadow-xl"
            >
              Doorgaan met gesprek
            </button>
          </>
        )}
      </div>
    </div>
  );
}
